import React, { useEffect, useState } from 'react';
import { Element } from 'react-scroll';
import './About.css';
import { motion } from 'framer-motion';
import { fadeInUp, staggerContainer, cardVariant, slideInLeft, slideInRight, textReveal } from '../../components/functions/animation';
import { FaLink, FaDumbbell, FaTrophy, FaBolt, FaMapMarkerAlt } from 'react-icons/fa';
import { FaGraduationCap } from 'react-icons/fa6';
import { BsBookFill } from 'react-icons/bs';
import { FiDownload, FiEye, FiFileText, FiX } from 'react-icons/fi';
import { AboutMe, education, verifiedCredentials } from '../../data/about';

const SECTION_ICONS = {
  'Certificates': <FaGraduationCap />,
  'Hobbies & Interests': <FaDumbbell />,
  'Achievements': <FaTrophy />,
  'Strengths & Weakness': <FaBolt />,
};

const About = () => {
  const [activeCredential, setActiveCredential] = useState(null);

  useEffect(() => {
    if (!activeCredential) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setActiveCredential(null);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [activeCredential]);

  return (
    <Element name="about">
      <section id="about">
        <div className="container">
          {/* Heading */}
          <motion.div variants={fadeInUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
            <p className="section-label">Who I Am</p>
            <h2 className="heading">About <span>Me</span></h2>
          </motion.div>

          {/* Bio */}
          <motion.div
            className="about-bio-card"
            variants={fadeInUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
          >
            <div className="bio-accent-bar" />
            <motion.div
              className="bio-text"
              variants={staggerContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
            >
              <motion.p variants={textReveal}>I'm <strong>Obaidullah Zeeshan</strong>, a Full-Stack Developer with MERN stack expertise and a growing passion for AI/ML. I enjoy crafting scalable applications and intelligent tools like AI podcast generators.</motion.p>
              <motion.p variants={textReveal}>Since 2019, I've evolved from basic scripting to building full-stack platforms, managing deployments, and experimenting with AI tools. I thrive at the intersection of creativity and logic—building user-focused, performant, and scalable solutions.</motion.p>
              <motion.p variants={textReveal}>My journey is one of continuous learning and curiosity. I believe in improving not just technically, but also in how I collaborate, adapt, and contribute to meaningful work.</motion.p>
            </motion.div>
          </motion.div>

          {/* Pull-quote */}
          <motion.div
            className="about-quote"
            variants={fadeInUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            <blockquote>"Every line of code is a small act of creation — and I intend every one to matter."</blockquote>
            <cite>— Obaidullah Zeeshan</cite>
          </motion.div>

          {/* Education */}
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="education-wrap"
          >
            <div className="edu-section-label">
              <BsBookFill className="edu-section-icon" />
              Education
            </div>
            {education.map((edu, i) => (
              <motion.div
                className="edu-card"
                key={i}
                variants={i % 2 === 0 ? slideInLeft : slideInRight}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.25 }}
                whileHover={{ y: -5, boxShadow: '0 16px 48px rgba(100,255,218,0.10)' }}
                whileTap={{ scale: 0.985 }}
                transition={{ type: 'spring', stiffness: 240, damping: 22 }}
              >
                <div className="edu-icon-col">
                  <div className="edu-icon-circle"><FaGraduationCap /></div>
                  {i < education.length - 1 && <div className="edu-line" />}
                </div>
                <div className="edu-content">
                  <div className="edu-header">
                    <div>
                      <h3 className="edu-degree">{edu.degree}</h3>
                      <p className="edu-school">{edu.school}</p>
                    </div>
                    <div className="edu-meta">
                      <span className="edu-duration">{edu.duration}</span>
                      {edu.score && (
                        <span className="edu-score">
                          <span className="edu-score-label">{edu.scoreLabel}</span>
                          <strong>{edu.score}</strong>
                        </span>
                      )}
                      <span className="edu-location"><FaMapMarkerAlt />{edu.location}</span>
                    </div>
                  </div>
                  <ul className="edu-highlights">
                    {edu.highlights.map((h, j) => (
                      <li key={j}>{h}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Cards grid */}
          <motion.div
            className="about-cards-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
          >
            {AboutMe.map((section, idx) => (
              <motion.div
                className="about-card"
                key={idx}
                custom={idx}
                variants={cardVariant}
                whileHover={{ y: -6, scale: 1.015 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 260, damping: 20 }}
              >
                <div className="about-card-header">
                  <span className="about-card-icon">{SECTION_ICONS[section.title] || <FaBolt />}</span>
                  <h3 className="about-card-title">{section.title}</h3>
                </div>
                <ul className="about-card-list">
                  {section.items.map((item, i) => (
                    typeof item === 'string' ? (
                      <li key={i} className="about-list-item">{item}</li>
                    ) : (
                      <li key={i} className="cert-item">
                        <span className="cert-label">{item.label}</span>
                        <span className="cert-meta">
                          <span className="cert-note">{item.note}</span>
                          {item.link && (
                            <a href={item.link} target="_blank" rel="noopener noreferrer" className="cert-link" aria-label={`View certificate: ${item.label}`}>
                              <FaLink />
                            </a>
                          )}
                        </span>
                      </li>
                    )
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>

          {/* Verified credentials */}
          <motion.div
            className="credentials-wrap"
            variants={fadeInUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
          >
            <div className="edu-section-label">
              <FiFileText className="edu-section-icon" />
              Verified Credentials
            </div>
            <p className="credentials-subtitle">
              Official education and experience documents for quick review.
            </p>

            <div className="credentials-grid">
              {verifiedCredentials.map((credential, idx) => (
                <motion.article
                  key={credential.title}
                  className="credential-card"
                  variants={cardVariant}
                  custom={idx}
                  whileHover={{ y: -5, scale: 1.012 }}
                  transition={{ type: 'spring', stiffness: 240, damping: 20 }}
                >
                  <button
                    type="button"
                    className="credential-preview"
                    onClick={() => setActiveCredential(credential)}
                    aria-label={`Preview ${credential.title}`}
                  >
                    <iframe
                      src={`${credential.link}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                      title={`Preview: ${credential.title}`}
                      loading="lazy"
                    />
                    <span className="credential-preview-overlay">
                      <FiEye />
                      Preview
                    </span>
                  </button>

                  <div className="credential-content">
                    <div className="credential-top">
                      <span className="credential-type">{credential.type}</span>
                      <span className="credential-period">{credential.period}</span>
                    </div>
                    <h3>{credential.title}</h3>
                    <p className="credential-issuer">{credential.issuer}</p>
                    <p className="credential-description">{credential.description}</p>
                  </div>

                  <div className="credential-actions">
                    <button
                      type="button"
                      className="credential-btn credential-btn-preview"
                      onClick={() => setActiveCredential(credential)}
                    >
                      <FiEye />
                      Preview
                    </button>
                    <a
                      href={credential.link}
                      className="credential-btn credential-btn-download"
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                    >
                      <FiDownload />
                      Download
                    </a>
                  </div>
                </motion.article>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {activeCredential && (
        <div
          className="credential-modal-backdrop"
          role="presentation"
          onClick={() => setActiveCredential(null)}
        >
          <div
            className="credential-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`${activeCredential.title} document preview`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="credential-modal-header">
              <div>
                <p className="credential-modal-kicker">{activeCredential.type}</p>
                <h3>{activeCredential.title}</h3>
              </div>
              <button
                type="button"
                className="credential-modal-close"
                onClick={() => setActiveCredential(null)}
                aria-label="Close certificate preview"
              >
                <FiX />
              </button>
            </div>
            <iframe
              className="credential-modal-frame"
              src={`${activeCredential.link}#view=FitH`}
              title={activeCredential.title}
            />
            <div className="credential-modal-actions">
              <a
                href={activeCredential.link}
                className="credential-btn credential-btn-download"
                target="_blank"
                rel="noopener noreferrer"
                download
              >
                <FiDownload />
                Download PDF
              </a>
            </div>
          </div>
        </div>
      )}
    </Element>
  );
};

export default About;
